<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Modal from 'dashboard/components/Modal.vue';
import LeadScoreBadge from 'dashboard/components-next/pipeline/LeadScoreBadge.vue';

const props = defineProps({
  pipelineCard: {
    type: Object,
    required: true,
  },
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'updated']);

const store = useStore();
const { t } = useI18n();

const newScore = ref(props.pipelineCard.lead_score || 0);

watch(
  () => props.show,
  val => {
    if (val) newScore.value = props.pipelineCard.lead_score || 0;
  }
);
const isSubmitting = ref(false);

const newCategory = computed(() => {
  if (newScore.value >= 70) return 'hot';
  if (newScore.value >= 40) return 'warm';
  return 'cold';
});

async function submitOverride() {
  isSubmitting.value = true;
  try {
    await store.dispatch('pipelineCards/overrideLeadScore', {
      itemId: props.pipelineCard.id,
      score: newScore.value,
    });

    emit('updated');
    emit('close');
  } catch (error) {
    // TODO: Show error toast
  } finally {
    isSubmitting.value = false;
  }
}

function handleClose() {
  if (!isSubmitting.value) {
    emit('close');
  }
}
</script>

<template>
  <Modal :show="show" @close="handleClose">
    <div class="p-6">
      <h3 class="text-heading-2 text-n-slate-12 mb-4">
        {{ t('PIPELINE.LEAD_SCORE.OVERRIDE_TITLE') }}
      </h3>

      <div class="space-y-6">
        <!-- Current Score -->
        <div>
          <label class="block text-label text-n-slate-12 mb-2">
            {{ t('PIPELINE.LEAD_SCORE.CURRENT_SCORE') }}
          </label>
          <LeadScoreBadge
            :score="pipelineCard.lead_score || 0"
            :factors="pipelineCard.lead_score_factors"
            show-label
          />
        </div>

        <!-- Slider -->
        <div>
          <label class="block text-label text-n-slate-12 mb-2">
            {{ t('PIPELINE.LEAD_SCORE.NEW_SCORE') }}
          </label>
          <input
            v-model.number="newScore"
            type="range"
            min="0"
            max="100"
            step="5"
            class="w-full h-2 bg-n-alpha-2 rounded-lg appearance-none cursor-pointer"
          />
          <div class="flex items-center justify-between mt-2">
            <span class="text-label-small text-n-slate-11">{{ 0 }}</span>
            <div class="text-center">
              <div class="text-3xl font-bold text-n-slate-12">
                {{ newScore }}
              </div>
              <div class="text-label-small text-n-slate-11 uppercase">
                {{ t(`PIPELINE.LEAD_SCORE.${newCategory.toUpperCase()}_LEAD`) }}
              </div>
            </div>
            <span class="text-label-small text-n-slate-11">{{ 100 }}</span>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex justify-end gap-3 mt-6">
        <button
          type="button"
          class="px-4 py-2 text-label font-medium text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12 rounded-lg transition-colors"
          @click="handleClose"
        >
          {{ t('PIPELINE.CANCEL') }}
        </button>
        <button
          type="button"
          :disabled="isSubmitting"
          class="px-4 py-2 text-label font-medium text-white bg-n-brand rounded-lg hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          @click="submitOverride"
        >
          {{ isSubmitting ? t('PIPELINE.LOADING') : t('PIPELINE.SAVE') }}
        </button>
      </div>
    </div>
  </Modal>
</template>
